import { useEffect, useRef } from 'react';
import type { DisplayAnnotation } from '@/lib/api/annotations';
import { ReactionBar } from './ReactionBar';
import { ReplyThread } from './ReplyThread';

export type AnnotationFilter = 'all' | 'public' | 'private' | 'orphaned';

interface Props {
  annotations: DisplayAnnotation[];
  orphaned?: DisplayAnnotation[];
  onClose: () => void;
  onAnnotationClick: (ann: DisplayAnnotation) => void;
  activeId: string | null;
  filter: AnnotationFilter;
  onFilterChange: (f: AnnotationFilter) => void;
}

function PanelCard({
  ann,
  active,
  orphan,
  onClick,
  setRef,
}: {
  ann: DisplayAnnotation;
  active: boolean;
  orphan: boolean;
  onClick: () => void;
  setRef?: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={setRef}
      className={`flex flex-col gap-1.5 rounded-r-lg border-l-[3px] px-3 py-2.5 transition-all ${
        orphan ? 'opacity-60' : active ? 'bg-blue-50 shadow-sm' : 'cursor-pointer bg-gray-50 hover:bg-blue-50 hover:shadow-sm'
      }`}
      style={{ borderLeftColor: ann.color }}
    >
      <div onClick={onClick} className={active ? undefined : 'cursor-pointer'}>
        {orphan && (
          <div className="mb-1 flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wide text-amber-500">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Anchor lost — text may have changed
          </div>
        )}
        <p className="font-serif text-xs italic leading-snug text-gray-500">"{ann.excerpt}"</p>
        <p className="mt-1 text-sm leading-relaxed text-gray-900">{ann.bodyMd}</p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[0.58rem] font-bold text-white"
            style={{ background: ann.color }}
          >
            {ann.initials}
          </div>
          <span className="text-xs text-gray-500">
            {ann.authorName} · {new Date(ann.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          {!ann.isPublic && <span className="ml-auto text-[0.7rem]">🔒</span>}
        </div>
      </div>

      {active && (
        <div className="mt-1 border-t border-gray-200 pt-2" onClick={e => e.stopPropagation()}>
          <ReactionBar targetType="annotation" targetId={ann.id} />
          <ReplyThread annotationId={ann.id} />
        </div>
      )}
    </div>
  );
}

export function AnnotationPanel({
  annotations,
  orphaned = [],
  onClose,
  onAnnotationClick,
  activeId,
  filter,
  onFilterChange,
}: Props) {
  const activeCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeId]);

  const orphanedIds = new Set(orphaned.map(a => a.id));
  const totalCount = annotations.length + orphaned.length;

  const shown =
    filter === 'orphaned' ? orphaned
    : filter === 'public' ? annotations.filter(a => a.isPublic)
    : filter === 'private' ? annotations.filter(a => !a.isPublic)
    : annotations;

  const emptyLabel =
    filter === 'orphaned' ? 'No orphaned annotations'
    : filter === 'public' ? 'No public annotations yet'
    : filter === 'private' ? 'No private annotations yet'
    : 'No annotations yet';

  return (
    <div className="flex h-full w-[355px] max-w-[92vw] flex-col bg-white shadow-[-4px_0_28px_rgba(0,0,0,0.14)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3.5">
        <div className="flex items-center gap-2">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#2f3e46" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span className="text-sm font-bold">Annotations</span>
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-[0.65rem] font-bold text-gray-500">
            {totalCount}
          </span>
          {orphaned.length > 0 && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[0.62rem] font-semibold text-amber-600">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              {orphaned.length} orphaned
            </span>
          )}
        </div>
        <button onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-100">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Filter */}
      <div className="flex shrink-0 flex-wrap gap-1.5 border-b border-gray-200 px-3.5 py-2.5">
        {(['all', 'public', 'private'] as const).map(f => (
          <button
            key={f}
            onClick={() => onFilterChange(f)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-all ${
              filter === f
                ? 'border-[#2f3e46] bg-[#2f3e46] text-white'
                : 'border-gray-200 bg-white text-gray-500 hover:border-[#2f3e46] hover:text-gray-900'
            }`}
          >
            {f === 'all' ? 'All' : f === 'public' ? '👁 Public' : '🔒 Private'}
          </button>
        ))}
        {orphaned.length > 0 && (
          <button
            onClick={() => onFilterChange('orphaned')}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-all ${
              filter === 'orphaned'
                ? 'border-amber-500 bg-amber-500 text-white'
                : 'border-amber-200 bg-amber-50 text-amber-600 hover:border-amber-500'
            }`}
          >
            ⚠ Orphaned
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto p-2.5">
        {shown.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-gray-400">{emptyLabel}</div>
        )}
        {shown.map(ann => (
          <PanelCard
            key={ann.id}
            ann={ann}
            active={activeId === ann.id}
            orphan={orphanedIds.has(ann.id)}
            onClick={() => onAnnotationClick(ann)}
            setRef={activeId === ann.id ? (el) => { activeCardRef.current = el; } : undefined}
          />
        ))}
      </div>

      <div className="shrink-0 border-t border-gray-200 px-4 py-3">
        <p className="text-center text-xs text-gray-400">Select text in the reader to add an annotation</p>
      </div>
    </div>
  );
}
